/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 *
 * TdStorylist — the dashboard's main-pane story list. Three render
 * states: skeleton (loading), empty (`<td-nodata>`), and the populated
 * list.
 *
 * Lit light-DOM component (#206). Story rows land via `${...}` text
 * bindings (always textContent, never HTML), preserving the safety
 * guarantee of the vanilla DOMParser path for future server-supplied
 * `type` / `content` strings.
 */

import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import {
  getStoryList,
  loadStory,
  type StorySummary,
} from '../services/story.js';
import { t, LocaleController } from '../services/i18n.js';

const FALLBACK_CONTENT =
  'Lorem ipsum odor amet, consectetuer adipiscing elit. Eget habitant ' +
  'scelerisque lectus ultrices nascetur aliquet sapien primis. Cursus ' +
  'sapien fusce semper nulla elit sociosqu lectus per sem. Sem ad ' +
  'porttitor dictum nisl pharetra tortor convallis. Sit molestie ' +
  'hendrerit porta dictum tortor posuere euismod magna. Mauris ' +
  'suspendisse pharetra finibus; eleifend etiam ridiculus.';

const randomId = (): string =>
  'story-' + Math.random().toString(36).substring(2, 9);

@customElement('td-storylist')
export class TdStorylist extends LitElement {
  @state() stories: StorySummary[] = [];
  @state() loading = true;

  /** Re-renders this component when the UI locale changes (#89). */
  readonly localeController = new LocaleController(this);

  override createRenderRoot(): HTMLElement {
    return this;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    void this.loadStories();
  }

  async loadStories(): Promise<void> {
    try {
      this.stories = await getStoryList();
    } catch (error) {
      console.error('Error loading stories:', error);
      this.stories = [];
    } finally {
      this.loading = false;
    }
  }

  override render(): TemplateResult {
    if (this.loading) {
      return this._renderSkeleton();
    }
    if (this.stories.length === 0) {
      return this._renderEmptyState();
    }
    return this._renderStoryList();
  }

  private _renderSkeleton(): TemplateResult {
    return html`
      <section id="disclosurelist">
        ${[0, 1, 2].map(
          () => html`
            <wa-skeleton>
              <wa-card>
                <wa-skeleton-item></wa-skeleton-item>
                <wa-skeleton-item></wa-skeleton-item>
              </wa-card>
            </wa-skeleton>
          `
        )}
      </section>
    `;
  }

  private _renderEmptyState(): TemplateResult {
    return html`
      <h1>${t('storylist.title')}</h1>
      <td-nodata></td-nodata>
    `;
  }

  /**
   * Open a story from the list (#240): hand the capability-token
   * reference to `loadStory`, which fetches + decrypts and mounts the
   * story form in the main slot. Only wired for rows whose disclosure
   * carries `storyId`/`storyChecksum` (app-backend#155).
   */
  private _openStory(story: StorySummary): void {
    if (story.storyId === undefined || story.storyChecksum === undefined) {
      return;
    }
    const target = document.querySelector<HTMLElement>('#main-insertion-point');
    if (!target) return;
    void loadStory(target, null, {
      storyId: story.storyId,
      checksum: story.storyChecksum,
    }).catch((error: unknown) => {
      console.error('Error opening story:', error);
    });
  }

  /**
   * Enter / Space on an openable card mirrors the click affordance so
   * keyboard users can open stories (#243). Non-openable cards never
   * receive this handler (no `role`/`tabindex` either — see render).
   */
  private _handleCardKeyDown(event: KeyboardEvent, story: StorySummary): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this._openStory(story);
    }
  }

  private _renderStoryList(): TemplateResult {
    return html`
      <h1>${t('storylist.title')}</h1>
      <section id="disclosurelist">
        ${this.stories.map(story => {
          const openable =
            story.storyId !== undefined && story.storyChecksum !== undefined;
          const type = story.type ?? t('storylist.defaultType');
          // Openable cards act as buttons: focusable, named, and
          // keyboard-actionable (#243). Static cards stay plain.
          return html`
            <wa-card
              id=${story.id ?? randomId()}
              class=${openable ? 'story-openable' : ''}
              role=${openable ? 'button' : nothing}
              tabindex=${openable ? '0' : nothing}
              aria-label=${
                openable ? t('storylist.openStory', { type }) : nothing
              }
              @click=${() => this._openStory(story)}
              @keydown=${(e: KeyboardEvent) => this._handleCardKeyDown(e, story)}
            >
              <h2>${type}</h2>
              <p>${story.content || FALLBACK_CONTENT}</p>
            </wa-card>
          `;
        })}
      </section>
    `;
  }
}
